class RoDetailsBar extends HTMLElement {
  constructor() {
    super();
    this.details = null;
    this.titleElement = null;
    this.ctaElementsWrapper = null;
    this.ctaElements = null;
    this.detailsContentContainer = null;
    this.detailsContentElement = null;
    this.documentInteractiveElements = null;
  }

  connectedCallback() {
    this.details = this.querySelector('details');

    if (! this.details) {
      console.error('Details bar details missing');
      return;
    }

    this.titleElement = this.details.querySelector('.js-details_bar-title');
    this.detailsContentContainer = this.querySelector('.js-details_bar-sidebar-container');
    this.detailsContentElement = this.querySelector('.js-details_bar-block-content');
    this.ctaElementsWrapper = this.querySelector('.js-details-bar-cta-wrapper');

    if (! this.titleElement || ! this.detailsContentContainer || ! this.detailsContentElement || ! this.ctaElementsWrapper) {
      console.error('Details bar invalid setup');
      return;
    }

    this.ctaElements = this.ctaElementsWrapper.querySelectorAll('.js-details_bar-cta');

    // When the bar is always sticky its content is static
    if (this.dataset.mobileMode == 'sticky-bottom' && this.dataset.desktopMode == 'sticky-bottom') {
      this.setStaticContent();
      return;
    }

    // When the bar is for mobile devices
    if (this.dataset.desktopMode == 'hidden') {
      this.updateMobileInfo();
      return;
    }

    // Add event listener for window resize
    window.addEventListener('resize', this.handleResize.bind(this));

    this.documentInteractiveElements = document.querySelectorAll('[data-details-bar]');

    // At the moment only for the index template the details bar info needs to be updated when the images are hovered
    if (this.dataset.template !== 'index') {
      this.initIntersectionObserver(this.documentInteractiveElements);
    } else {
      this.initHoverMode(this.documentInteractiveElements);

      // Find the best image based on visibility threshold
      const threshold = 0.6; // 60% visibility
      const visibleElement = Array.from(this.documentInteractiveElements).find(element => {
        const rect = element.getBoundingClientRect();
        const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
        return (visibleHeight / rect.height) >= threshold;
      });
  
      // Update the bar with the most visible element or fallback to the first
      this.updateDetailsBar(visibleElement || this.documentInteractiveElements[0]);
    }
  }

  disconnectedCallback() {
    if (! this.documentInteractiveElements) {
      return;
    }

    this.initIntersectionObserver(this.documentInteractiveElements, false);
    this.initHoverMode(this.documentInteractiveElements, false);
  }
  
  // Handle window resize event
  handleResize() {
    // Recalculate the height and trigger the transition
    const height = this.detailsContentElement.scrollHeight + 32; // Add padding if needed
    this.animateHeightTransition(height);
  }

  // When the content of the bar is the same for all media
  setStaticContent() {
    const staticContent = document.getElementById('RoMediaDetailsBarStaticContent');

    this.updateTitle(staticContent.dataset.title);
    this.updateCTAs(staticContent.dataset);
    this.updateContent(staticContent.innerHTML);
  }

  updateTitle(staticContentTitle = null, currentInteractiveElementTitle = null) {
    if (staticContentTitle) {
      this.titleElement.textContent = staticContentTitle;

      return;
    }

    if (currentInteractiveElementTitle) {
      if (this.titleElement.textContent === currentInteractiveElementTitle) {
        return;
      }

      this.titleElement.style.opacity = 0;

      setTimeout(() => {
        this.titleElement.textContent = currentInteractiveElementTitle;
        this.titleElement.style.opacity = 100;
      }, 300);
    }
  }

  updateCTAs(staticContentDataset = null, currentInteractiveElement = null) {
    this.ctaElements.forEach(function(ctaElement, i) {
      if (staticContentDataset) {

        const label = staticContentDataset['cta' + i];
        const link = staticContentDataset['cta' + i + 'Link'];

        if (label) {
          ctaElement.querySelector('span').textContent = label;
          ctaElement.setAttribute('title', label);
          ctaElement.classList.remove('tw-hidden');
        }
  
        if (link) {
          ctaElement.setAttribute('href', link);
  
          if (link[0] === '#') {
            ctaElement.classList.add('js-cta-icon');
          }
  
          if (window.location.pathname.startsWith('/pages/runway-shows-')) {
            this.handleRunwayShowCtaCustomStyle(ctaElement, link);
          }
        }

        return;
      }

      if (currentInteractiveElement) {
        const label = currentInteractiveElement.dataset['link' + i + 'Label'];
        const link = currentInteractiveElement.dataset['link' + i];

        if (label) {
          if (ctaElement.querySelector('span').textContent !== label) {
            this.ctaElementsWrapper.style.opacity = 0;

            setTimeout(() => {
              ctaElement.classList.remove('tw-hidden');
              ctaElement.querySelector('span').textContent = label;
            }, 300);
          }
        } else {
          ctaElement.classList.add('tw-hidden');
        }

        if (link) {
          if (ctaElement.getAttribute('href') !== link) {
            ctaElement.setAttribute('href', link);
          }
        }

        if (i === this.ctaElements.length - 1 && this.ctaElementsWrapper.style.opacity == 0) {
          setTimeout(() => {
            this.ctaElementsWrapper.style.opacity = 100;
          }, 300);
        }
      }
    }, this);
  }

  updateContent(staticContent = null, currentInteractiveElementContent = null, isMobileOnlyDetailsBar = false) {
    if (staticContent) {
      this.detailsContentElement.innerHTML = staticContent;

      return;
    }

    if (isMobileOnlyDetailsBar) {
      if (currentInteractiveElementContent) {
        this.detailsContentElement.innerHTML = currentInteractiveElementContent;
      } else {
        this.detailsContentContainer.remove();
      }

      return;
    }

    // If there is new content the content is updated and the container is expanded with a smooth animation
    // If no new content is provided, the container collapses and is hidden after the transition.
    if (currentInteractiveElementContent) {
      this.detailsContentContainer.classList.remove('tw-hidden');
      this.detailsContentElement.innerHTML = currentInteractiveElementContent;

      // Animate expansion
      if (this.details.open) {
        const height = this.detailsContentElement.offsetHeight + 32;
        this.animateHeightTransition(height);
      } else {
        // Workaround for Safari, where elements inside a closed <details> tag have a height of 0
        this.detailsContentContainer.style.visibility = 'hidden';
        this.details.open = true;
        const height = this.detailsContentElement.offsetHeight + 32;
        this.details.open = false;
        this.detailsContentContainer.style.visibility = null;
        this.animateHeightTransition(height);
      }
    } else {
      // Animate collapse if no content
      this.detailsContentElement.innerHTML = '';
      this.animateHeightTransition(0);
      setTimeout(() => {
        this.detailsContentContainer.classList.add('tw-hidden');
      }, 300);
    }
  }

  handleRunwayShowCtaCustomStyle(ctaElement, ctaLink) {
    if (window.location.pathname.startsWith(ctaLink)) {
      ctaElement.classList.add('tw-font-sans-medium-m');
      ctaElement.classList.remove('tw-font-sans-m', 'tw-text-darkgrey');
    } else {
      ctaElement.classList.add('tw-font-sans-m', 'tw-text-darkgrey');
      ctaElement.classList.remove('tw-font-sans-medium-m');
    }
  }

  updateMobileInfo() {
    const detailsContent = document.querySelectorAll(`[data-media-identifier="${this.dataset.identifierDetailsBar}"]`);
        
    let combinedTitle = '';
    let combinedHTML = '';
    detailsContent.forEach((node, index) => {
      if (node.dataset.furnituresTitleFormatted != '') {
        if (index != 0) {
          combinedTitle += ' | ';
        }
        combinedTitle += node.dataset.furnituresTitleFormatted;
      }
      combinedHTML += node.innerHTML;
    });

    this.updateTitle(null, combinedTitle);
    this.updateContent(null, combinedHTML, true);
  }

  initIntersectionObserver(documentInteractiveElements, init = true) {
    if (! init && this.observer) {
      this.observer.disconnect();
      return;
    }

    this.observer = new IntersectionObserver(this.onIntersection.bind(this), {
      root: null, // use the viewport as the root
      threshold: 0.5, // trigger when 50% of the element is in view
    });

    // Observe each interactive element
    documentInteractiveElements.forEach(element => {
      this.observer.observe(element); // Start observing the element
    });
  }

  onIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.updateDetailsBar(entry.target);
      }
    });
  }

  initHoverMode(documentInteractiveElements, init = true) {
    documentInteractiveElements.forEach(element => {
      if (init) {
        element.addEventListener('mouseover', this.boundMouseOver = () => this.onMouseOverInteractiveElement(element));
        element.addEventListener('mouseleave', this.boundMouseLeave = () => this.onMouseLeaveInteractiveElement());
      } else {
        element.removeEventListener('mouseover', this.boundMouseOver);
        element.removeEventListener('mouseleave', this.boundMouseLeave);
      }
    });

    if (init) {
      this.addEventListener('mouseover', this.boundMouseOverDetailsBar = () => this.onMouseOverDetailsBar());
    } else {
      this.removeEventListener('mouseover', this.boundMouseOverDetailsBar);
    }
  }

  onMouseOverInteractiveElement(element) {
    if (!window.matchMedia('(min-width: 1024px)').matches) {
      return;
    }

    this.tooFastOverTimeoutId = setTimeout(() => {
      this.overTimeoutId = setTimeout(() => {
        this.updateDetailsBar(element);
      }, 300);
    }, 400);
  }

  onMouseOverDetailsBar() {
    if (typeof this.overTimeoutId === 'undefined') {
      return;
    }

    if (!window.matchMedia('(min-width: 1024px)').matches) {
      return;
    }

    clearTimeout(this.overTimeoutId);
  }

  onMouseLeaveInteractiveElement() {
    if (typeof this.tooFastOverTimeoutId === 'undefined') {
      return;
    }

    if (!window.matchMedia('(min-width: 1024px)').matches) {
      return;
    }

    clearTimeout(this.tooFastOverTimeoutId);
  }

  updateDetailsBar(element) {
    const detailsContent = document.querySelectorAll(`[data-media-identifier="${element.dataset.identifierMediaBlock}"]`);
    
    let combinedTitle = '';
    let combinedHTML = '';
    detailsContent.forEach((node, index) => {
      if (node.dataset.furnituresTitleFormatted != '') {
        if (index != 0) {
          combinedTitle += ' | ';
        }
        combinedTitle += node.dataset.furnituresTitleFormatted;
      }
      combinedHTML += node.innerHTML;
    });

    let title = element.dataset.title ? element.dataset.title : combinedTitle;
    this.updateTitle(null, title);

    this.updateCTAs(null, element);
    this.updateContent(null, combinedHTML);
  }

  // Function to handle height transitions
  animateHeightTransition(height) {
    requestAnimationFrame(() => {
      this.detailsContentContainer.style.height = `${height}px`;
    });
  }
}

customElements.define('ro-details-bar', RoDetailsBar);

class RoPasswordToggle extends HTMLElement {
  constructor() {
    super();
    this.passwordInput = this.previousElementSibling.type === 'password' ? this.previousElementSibling : null;
    this.showPasswordCheckbox = this.querySelector('input[type=checkbox]');

    if (! this.passwordInput || ! this.showPasswordCheckbox) {
      return;
    }

    this.showPasswordCheckbox.addEventListener('change', () => {
      this.passwordInput.type = this.showPasswordCheckbox.checked ? 'text' : 'password';
    });
  }
}

customElements.define('ro-password-toggle', RoPasswordToggle);


class RoSizeSelector extends HTMLElement {
  constructor() {
    super();
    this.sizeButton = this.querySelectorAll('button');
  }

  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target.matches('button')) {
        this.sizeButton.forEach((button) => {
          button.parentElement?.classList.remove('tw-button-active');
        });
        event.target.parentElement?.classList.add('tw-button-active');
      }
    });
  }
}

customElements.define('ro-size-selector', RoSizeSelector);

class RoMediaVideoControls extends HTMLElement {
  constructor() {
    super();
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
  }

  connectedCallback() {
    this.videos = this.querySelectorAll('video');
    if (this.videos.length === 0) return;

    this.setAttribute('tabindex', '0');
    this.setAttribute('role', 'group');
    if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', 'Video. Press Space to pause or play.');
    }
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('focus', this.handleFocus, true);
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('focus', this.handleFocus, true);
  }

  handleFocus() {
    if (!this.videos) return;
    const hasFocusInside = this.contains(document.activeElement);
    this.videos.forEach((video) => {
      if (hasFocusInside) {
        video.style.outline = '2px solid #ffffff';
        video.style.outlineOffset = '4px';
      } else {
        video.style.outline = 'none';
        video.style.outlineOffset = '';
      }
    });
  }

  handleKeydown(event) {
    if (event.key !== ' ' && event.key !== 'Spacebar') return;

    const interactive = event.target.closest('a, button, [role="button"], input, select, textarea');
    if (interactive) return;

    event.preventDefault();
    this.videos.forEach((video) => {
      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }
}

customElements.define('ro-media-video-controls', RoMediaVideoControls);

class RoDialogAddToCart extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector("dialog.ro-add-to-cart-dialog");
    this.showButton = this.querySelector(".js-button--show");
    this.closeButton = this.querySelector(".js-button--close");
    this.sectionToObserve = this.closest(".js-ro-product-info");
    this.maxQtyNotificationDialog = this.querySelector("ro-notification .notification--variant-order-max-qty-reached");
    this.previouslyFocusedElement = null;
    this.handleDialogKeyDown = this.handleDialogKeyDown.bind(this);
  }

  connectedCallback() {
    if (this.dialog && this.showButton && this.closeButton) {
      this.handleOpenButtonClick();
      this.handleCloseButtonClick();

      subscribe(PUB_SUB_EVENTS.sizeGuideOpen, () => {
        this.closeDialog();
      });

      subscribe(PUB_SUB_EVENTS.sizeGuideClosed, () => {
        this.openDialog();
      });

      // Ensure dialog has basic accessibility attributes
      if (!this.dialog.hasAttribute('role')) {
        this.dialog.setAttribute('role', 'dialog');
      }
      this.dialog.setAttribute('aria-modal', 'true');

      if (this.sectionToObserve) {
        this.createObserver();
      }
    } else {
      console.error("Required elements not found. Check the component 'ro-dialog-add-to-cart'");
    }
  }

  openDialog() {
    if (this.dialog.open) {
      return;
    }

    this.previouslyFocusedElement = document.activeElement;
    this.dialog.show();
    if (typeof trapFocus === 'function') {
      trapFocus(this.dialog);
    }
    // Ensure the close button is the initial focus target for keyboard users
    if (this.closeButton && typeof this.closeButton.focus === 'function') {
      this.closeButton.focus();
    } else {
      this.dialog.focus();
    }
    this.dialog.addEventListener('keydown', this.handleDialogKeyDown);
    this.handleOutsideClick();
  }

  closeDialog() {
    if (!this.dialog.open) {
      return;
    }

    this.dialog.close();
    this.stopHandleOutsideClick();
    this.dialog.removeEventListener('keydown', this.handleDialogKeyDown);

    if (typeof removeTrapFocus === 'function') {
      const elementToFocus = this.previouslyFocusedElement || this.showButton;
      removeTrapFocus(elementToFocus);
    }

    this.previouslyFocusedElement = null;
  }

  handleOpenButtonClick() {
    this.showButton.addEventListener("click", () => {
      this.openDialog()
    });
  }

  handleCloseButtonClick() {
    this.closeButton.addEventListener("click", () => {
      this.closeDialog();
    });
  }

  // Close dialog if click happens outside the dialog
  handleOutsideClick() {
    this.documentClickListenerAbortController = new AbortController();

    document.addEventListener(
      "click",
      (event) => {
        const isClickInsideDialog = this.dialog.contains(event.target);
        const isClickOnShowButton = this.showButton.contains(event.target);
        const isDialogOpen = this.dialog.open;

        if (isDialogOpen && !isClickInsideDialog && !isClickOnShowButton) {
          this.closeDialog();
        }

        /**
         * Specific case, close the dialog if:
         * 1. The click is inside the this.maxQtyNotificationDialog and it's not open
         * 2. The dialog is open and the click is outside the dialog and the show button
         */
        const isMaxQtyNotificationOpen = this.maxQtyNotificationDialog && this.maxQtyNotificationDialog.classList.contains('js-open');
        if (!isMaxQtyNotificationOpen && this.maxQtyNotificationDialog.contains(event.target)) {
          this.closeDialog();
        }
      },
      {
        'signal': this.documentClickListenerAbortController.signal
      }
    );
  }

  stopHandleOutsideClick() {
    if (this.documentClickListenerAbortController) {
      this.documentClickListenerAbortController.abort();
      this.documentClickListenerAbortController = null;
    }
  }

  handleDialogKeyDown(event) {
    if (event.code?.toUpperCase() === 'ESCAPE' || event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog();
    }
  }

  // IntersectionObserver callback
  onIntersection(entries) {
    entries.forEach(entry => {
      if (!entry.isIntersecting) {
        // Close the dialog if it's open
        if (this.dialog.open) {
          this.closeDialog();
        }
      }
    });
  }

  // Initialize IntersectionObserver
  createObserver() {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.2,
    };

    const observer = new IntersectionObserver(this.onIntersection.bind(this), options);
    observer.observe(this.sectionToObserve);
  }
}

customElements.define('ro-dialog-add-to-cart', RoDialogAddToCart);

class RoSidebarBase extends HTMLElement {
  constructor() {
    super();
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
  }

  /*
  * Lifecycle method called when the component is connected to the DOM, used to setup the sidebar
  */
  connectedCallback() {
    this.setup();
  }

  /*
  * Setup the sidebar
  */
  setup() {
    // Store common elements
    this.el = this.querySelector("details");
    this.summary = this.el.querySelector('summary');

    // Bind event handlers
    this.onClick = this.onClick.bind(this);
    this.onClickOutside = this.onClickOutside.bind(this);
  }

  /*
  * Bind the events to the summary element and the close icon (if present)
  */
  bindEvents() {
    this.summary.addEventListener('click', this.onClick);

    if (this.closeButtons) {
      this.closeButtons.forEach(closeButton => {
        closeButton.addEventListener('click', this.hide.bind(this));
      });
    }
  }

  /*
  * Handle the click on the summary element
    @param {Event} e - The click event
  */
  onClick(e) {
    // Prevent the default behavior as the summary must open and close with a custom animated effect
    e.preventDefault();

    // Check if the element is being closed or is already closed
    if (this.isClosing || !this.el.open) {
      this.open();
      // Check if the element is being openned or is already open
    } else if (this.isExpanding || this.el.open) {
      this.hide();
    }
  }

  /*
  * Hide the sidebar
  */
  hide() {
    this.dataset.action = "close";
    this.isClosing = true;
    document.removeEventListener('click', this.onClickOutside);
    this.animateContent(false);
  }

  /*
  * Open the sidebar
  */
  open() {
    this.dataset.action = "open";
    this.el.open = true;
    requestAnimationFrame(() => {
      this.isExpanding = true;
      this.animateContent(true);
    });
  }

  /*
  * Animate the sidebar content
    @param {boolean} opening - The direction of the animation
  */
  animateContent(opening) {
    const startTranslate = opening ? `translateX(-100%)` : `translateX(0)`;
    const endTranslate = opening ? `translateX(0)` : `translateX(-100%)`;

    const animationTime = 300;
    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.content.animate({
      transform: [startTranslate, endTranslate],
      opacity: [opening ? 0 : 1, opening ? 1 : 0]
    }, {
      duration: animationTime,
      easing: 'ease-out'
    });

    this.animation.onfinish = () => this.onAnimationFinish(opening);
    this.animation.oncancel = () => (this.isClosing = this.isExpanding = false);
  }

  /*
  * Handle the end of the animation
    @param {boolean} open - The direction of the animation
  */
  onAnimationFinish(open) {
    this.el.open = open;
    this.animation = null;
    this.isClosing = this.isExpanding = false;

    if (open) {
      document.body.classList.add('sidebar-visible');
      // Add an event listener to detect clicks outside the sidebar
      document.addEventListener('click', this.onClickOutside);
    } else {
      document.body.classList.remove('sidebar-visible');
    }
  }

  /*
  * Detect clicks outside the sidebar and hide it
  */
  onClickOutside(e) {
    let isInsideSidebar = false;
    // Check if the click was inside the sidebar checking also for nested details elements (so iterating over the click target details parents)
    let target = e.target;
    while (target) {
      if (target === this.el) {
        isInsideSidebar = true;
        break;
      }
      target = target.parentElement?.closest('details');
    }
    if (!isInsideSidebar) {
      this.hide();
    }
  }
}

class RoSidebarMenu extends RoSidebarBase {
  setup() {
    super.setup();

    // Specific elements for the menu sidebar
    this.content = this.el.children[1];
    this.menus = this.content.querySelectorAll('[data-index]');
    this.submenus = this.content.querySelectorAll('.submenu');
    this.backButtons = this.content.querySelectorAll('.submenu-back');

    this.bindEvents();
    this.fetchMenu();
    
  }

  async fetchMenu() {
    const url = `/search?q=${this.dataset.handle}&view=menu`;
    
    const response = await fetch(url);
    const result = await response.text();

    const items = JSON.parse(result.replace(/,]/g, ']'));

    items.forEach(item => {
      if (item.productCount == 0 && item.type === 'collection_link') {
        document.querySelectorAll(`[data-menu-handle="${item.handle}"]`).forEach(menu => {
          menu.remove();
        })
      }
    });
    this.toggleAttribute('hidden', false);
  }


  /*
  * Bind the events to the header menu botton element, the menu items and the back buttons
  */
  bindEvents() {
    super.bindEvents();
    document.querySelector('.ro-header-menu-button').addEventListener('click', this.onClick);
    this.menus.forEach(menu => menu.addEventListener('click', this.onMenuClick.bind(this)));
    this.backButtons.forEach(back => back.addEventListener('click', this.onBackClick.bind(this)));
  }

  /*
  * Handle the click on a menu item and show the submenu
    @param {Event} e - The click event
  */
  onMenuClick(e) {
    const anchor = e.target.closest('a');
    const parentIndex = anchor.dataset.index;
    const targetSubmenuLevel = anchor.dataset.level;
    const submenuToShow = this.content.querySelector(`.submenu.tw-hidden[data-parent-index="${parentIndex}"]`);

    if (submenuToShow) {
      // Clicked on a root level menu item
      if (targetSubmenuLevel == 0) {
        const activeSubmenu = this.content.querySelector('.submenu:not(.tw-hidden)[data-level="1"]');

        // Hide the active submenu if it's not the same as the submenu we want to show
        if (activeSubmenu && activeSubmenu !== submenuToShow) {
          this.hideSubmenu(activeSubmenu);
        }
      } else {
        // Check for active submenus on the same level
        const activeSubmenu = this.content.querySelector(`.submenu:not(.tw-hidden)[data-parent-index="${parentIndex}"]`);

        // Hide the active submenu if it's not the same as the submenu we want to show
        if (activeSubmenu && activeSubmenu !== submenuToShow) {
          this.hideSubmenu(activeSubmenu);
        }
      }

      this.showSubmenu(submenuToShow);
    }
  }

  /*
  * Override the open method to add a class to the body
  */
  open() {
    super.open();
    document.body.classList.add('ro-header-menu-open');
  }

  /*
  * Override the hide method to remove the class to the body, the active class from the menu items and hide all the submenus
  */
  hide() {
    super.hide();
    document.body.classList.remove('ro-header-menu-open');

    // Hide all the active submenus
    this.submenus.forEach(submenu => {
      if (!submenu.classList.contains('tw-hidden')) {
        this.hideSubmenu(submenu);
      }
    });
  }

  /*
  * Override the onAnimationFinish method to remove the active class from the menu items and reset the sidebar width
    @param {boolean} open - The direction of the animation
  */
  onAnimationFinish(open) {
    super.onAnimationFinish(open);

    if (!open) {
      // Remove the active class from the menu items
      this.menus.forEach(menu => menu.classList.remove('active'));
      // Reset the content width
      this.content.classList.remove('lg:tw-w-480');
    }
  }

  /*
  * Show a submenu
    @param {HTMLElement} submenu - The submenu to show
  */
  showSubmenu(submenu) {
    if (submenu) {
      this.content.classList.add('lg:tw-w-480')
      this.content.querySelector(`a[data-index="${submenu.dataset.parentIndex}"]`)?.classList.add('active');
      submenu.classList.remove('tw-hidden');
      requestAnimationFrame(() => {
        submenu.classList.add('tw-opacity-100', 'tw-translate-x-0');
        submenu.classList.remove('tw-opacity-0', '-tw-translate-x-full');
      });
    }
  }

  /*
  * Manage the click on a back button and hide the submenu
    @param {Event} e - The click event
  */
  onBackClick(e) {
    const submenu = e.target.closest('.submenu');
    this.hideSubmenu(submenu);
  }

  /*
  * Hide a submenu
    @param {HTMLElement} submenu - The submenu to hide
  */
  hideSubmenu(submenu) {
    if (submenu) {
      this.content.querySelector(`a[data-index="${submenu.dataset.parentIndex}"]`)?.classList.remove('active');
      submenu.classList.remove('tw-opacity-100', 'tw-translate-x-0');
      submenu.classList.add('tw-opacity-0', '-tw-translate-x-full');
      submenu.addEventListener('transitionend', () => {
        submenu.classList.add('tw-hidden');
      }, { once: true });
    }
  }
}
class RoHeaderSearch extends RoSidebarBase {
  setup() {
    super.setup();

    // Specific elements for the search sidebar
    this.content = this.el.children[1];
    this.form = this.content.querySelector('form');
    this.lastSearchHistory = [];
    this.closeButtons = this.querySelectorAll('button[data-close]');
    this.predictiveSearch = this.querySelector('ro-predictive-search');

    this.bindEvents();
  }

  /*
  * Bind the events to the form element and the search button
  */
  bindEvents() {
    super.bindEvents();
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    document.querySelector('.ro-header-search-button').addEventListener('click', this.onClick);
  }

  /*
  * Handle the form submission and save the search value in the local storage
    @param {Event} e - The submit event
  */
  onSubmit(e) {
    e.preventDefault();
    const searchValue = this.form.querySelector('input[type="search"]').value.trim();
    this.saveSearchHistory(searchValue);
    this.form.submit();
  }

  /*
  * Override the open method to add a class to the body and update the search history
  */
  open() {
    super.open();
    document.body.classList.add('ro-header-search-open');

    // Get the search history from the local storage
    const headerSearchHistory = JSON.parse(localStorage.getItem('roHeaderSearchHistory')) || [];
    // Confront the search history with the last search history
    // If the search history is different, update the last search history
    if (JSON.stringify(headerSearchHistory) !== JSON.stringify(this.lastSearchHistory)) {
      this.updateSearchHistory(headerSearchHistory);
    }

    this.setFocusOnSearchInput();
  }

  setFocusOnSearchInput() {
    this.predictiveSearch.input.focus();
    this.predictiveSearch.input.setSelectionRange(this.predictiveSearch.input.value.length, this.predictiveSearch.input.value.length);
  }

  /*
  * Update the search history list
    @param {Array} headerSearchHistory - The search history to update
  */
  updateSearchHistory(headerSearchHistory) {
    this.lastSearchHistory = headerSearchHistory;
    // Clear the search history list
    this.querySelector('.ro-header-search-history').innerHTML = '';
    this.querySelector('.ro-header-search-history-wrapper').classList.remove('tw-hidden');
    // Create a new list of search history
    headerSearchHistory.forEach((search) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `/search?q=${search}`;
      a.className = 'tw-font-sans-m lg:tw-cta';
      const div = document.createElement('div');
      const p = document.createElement('p');
      p.textContent = search;
      div.appendChild(p);
      a.appendChild(div);
      li.appendChild(a);
      this.content.querySelector('.ro-header-search-history').appendChild(li);
    });
  }

  /*
  * Override the hide method to remove the class to the body
  */
  hide() {
    super.hide();
    document.body.classList.remove('ro-header-search-open');
  }

  /*
  * Save the search value in the local storage
    @param {string} searchValue - The search value to save
  */
  saveSearchHistory(searchValue) {
    let headerSearchHistory = JSON.parse(localStorage.getItem('roHeaderSearchHistory')) || [];
    // Check if the search value is already in the search history
    if (!headerSearchHistory.includes(searchValue)) {
      // Add the search value to the search history
      headerSearchHistory.unshift(searchValue);
      // Limit the search history to 5 elements
      headerSearchHistory = headerSearchHistory.slice(0, 5);
    }
    // Save the search history in the local storage
    localStorage.setItem('roHeaderSearchHistory', JSON.stringify(headerSearchHistory));
  }
}

customElements.define('ro-sidebar-menu', RoSidebarMenu);
customElements.define('ro-header-search', RoHeaderSearch);

class RoSidebarDetailsBar extends RoSidebarBase {
  setup() {
    super.setup();

    this.detailsBarTitle = this.querySelector('.js-details_bar-title');
    this.content = this.querySelector('.js-details_bar-sidebar-container');
    this.closeButtons = this.querySelectorAll('button.btn-close');
    this.openButtons = this.querySelectorAll('.js-details_bar-cta');

    this.bindEvents();
  }

  bindEvents() {
    super.bindEvents();

    if (this.openButtons) {
      this.openButtons.forEach(function(openButton) {
        openButton.addEventListener('click', this.onCTAClick);
      }, this);
    }

    if (this.content) {
      this.content.addEventListener('click', this.onContentClick.bind(this));
    }
  }

  onCTAClick(e) {
    if (this.getAttribute('href') === null || this.getAttribute('href') === '') {
      e.preventDefault();
    } else {
      e.stopPropagation();
    }
  }

  onContentClick(e) {
    if (e.target.classList.contains('js-details_bar_content-cta')) {
      this.hide();
    }
  }

  /*
  * Include CTA links outside <details> when detecting outside clicks
    @param {Event} e - The click event
  */
  onClickOutside(e) {
    if (!this.contains(e.target)) {
      this.hide();
    }
  }

  /*
  * Override the method
  * Animate the sidebar content
    @param {boolean} opening - The direction of the animation
  */
  animateContent(opening) {
    if (this.content) {
      const startTranslate = opening ? `translateY(100%)` : `translateY(0)`;
      const endTranslate = opening ? `translateY(0)` : `translateY(100%)`;
  
      const animationTime = 300;
      if (this.animation) {
        this.animation.cancel();
      }
  
      this.animation = this.content.animate({
        transform: [startTranslate, endTranslate],
        opacity: [opening ? 0 : 1, opening ? 1 : 0]
      }, {
        duration: animationTime,
        easing: 'ease-out'
      });
  
      this.animation.onfinish = () => this.onAnimationFinish(opening);
      this.animation.oncancel = () => (this.isClosing = this.isExpanding = false);
    }
  }

  /*
  * Handle the end of the animation
    @param {boolean} open - The direction of the animation
  */
  onAnimationFinish(open) {
    this.el.open = open;
    this.animation = null;
    this.isClosing = this.isExpanding = false;

    if (open) {
      // Add an event listener to detect clicks outside the sidebar
      document.addEventListener('click', this.onClickOutside);
    }
  }
}
customElements.define('ro-sidebar-details-bar', RoSidebarDetailsBar);

class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    const container = this.dataset.container;
    if (!container) return;
    
    this.container = document.querySelector(container);
    this.totalPages = this.dataset.totalPages || 1;
    this.bufferedPage = { key: null, value: null };
    this.selector = this.dataset.selector;

    this.utils = {
      isElementVisible: (el) => {
        const rect = el.getBoundingClientRect();
        return (
          rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
          rect.bottom >= 0
        );
      }
    };
  }

  connectedCallback() {
    this.handleCurrentPagination();
    this.listener();

    history.scrollRestoration = "auto";
  }

  listener() {
    window.addEventListener("scroll", () => {
      if (
        this.utils.isElementVisible(this) &&
        !this.classList.contains("tw-hidden")
      ) {
        this.handleAction();
      }
    });
  }

  handleAction() {
    const url = new URL(location);
    const curPage = parseInt(url.searchParams.get("pagination")) || 1;
    url.searchParams.set("pagination", curPage + 1);
    history.pushState(null, null, url.toString());
    this.renderContent();
    this.createBufferedPage(curPage + 2);
  }

  async createBufferedPage(bufferPage) {
    const url = new URL(location);
    url.searchParams.set("page", bufferPage);
    const response = await fetch(url);

    this.bufferedPage.key = url.href;
    this.bufferedPage.value = await response.text();
  }

  getCurrentPage() {
    const url = new URL(window.location);
    return parseInt(url.searchParams.get("pagination") || 1);
  }

  async handleCurrentPagination() {
    try {
      const page = this.getCurrentPage();
      if (page) {
        for (let i = 2; i <= page; i++) {
          const url = new URL(window.location);
          url.searchParams.set("pagination", parseInt(i));
          history.pushState(null, null, url.toString());
          await this.renderContent();
        }
      }


      this.createBufferedPage(this.getCurrentPage() + 1);
    } catch (err) { }
  }

  getNextPage() {
    const href = window.location.href;
    return href.replace("pagination", "page");
  }

  async renderContent() {
    this.classList.add('tw-hidden');
    const page = this.getCurrentPage();
    const href = window.location.href;

    let html;

    if (this.bufferedPage.key === href) {
      html = this.bufferedPage.value;
    } else {
      const response = await fetch(this.getNextPage());
      html = await response.text();
    }

    const doc = new DOMParser().parseFromString(html, "text/html");
    const items = doc.querySelectorAll(this.selector);
    
    if (items.length > 0) {
      items.forEach(item => {
        this.container.appendChild(item);
      });
    }

    this.classList.toggle("tw-hidden", page >= parseInt(this.totalPages));
  }
}

customElements.define('infinite-scroll', InfiniteScroll);


class InfiniteScrollRunway extends InfiniteScroll {
  constructor() {
    super();
    this.urls = JSON.parse(this.querySelector('[data-pages]')?.innerHTML || '[]');
  }

  connectedCallback() {
    super.connectedCallback();
  }

  getNextPage() {
    const page = this.getCurrentPage() - 2;
    return this.urls[page];

  }
}
customElements.define('infinite-scroll-runway', InfiniteScrollRunway);
